import type { useNavigate } from '@tanstack/react-router';

/**
 * Sanitize a `?next=` redirect target so login can only bounce users back to
 * an in-app path (open-redirect guard). Returns the normalized path
 * (pathname + search + hash) or `undefined` when the value is unsafe, not a
 * string, or just the home path (the post-login default anyway).
 */
export function sanitizeNextPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.startsWith('/')) return undefined;

  let url: URL;
  try {
    // The URL parser normalizes tricks like '/\evil.com' → '//evil.com',
    // which then fails the same-origin check below.
    url = new URL(value, window.location.origin);
  } catch {
    return undefined;
  }
  if (url.origin !== window.location.origin) return undefined;

  const path = url.pathname + url.search + url.hash;
  // Dot-segments like '/..//evil.com' resolve same-origin but normalize to a
  // protocol-relative pathname ('//evil.com') — reject so the reconstructed
  // value can't be used as an off-origin target.
  if (path.startsWith('//')) return undefined;
  return path === '/' ? undefined : path;
}

/**
 * `validateSearch` for routes that carry a `?next=` redirect target — sanitizes
 * the param so the route only ever sees a safe in-app path or `undefined`.
 */
export function validateNextSearch(search: Record<string, unknown>): {
  next?: string;
} {
  return { next: sanitizeNextPath(search.next) };
}

/**
 * Whether a post-auth destination has to be reached by a full document load.
 *
 * Only OAuth does, and the reason is caution rather than a known failure.
 * The reload was added for the prebuilt `IdentityProvider`, which wraps its
 * consent screen in a check that a member is already loaded. That screen is
 * gone (ENG-2675): the headless consent page reads the session cookie on each
 * Stytch request (`NetworkClient.fetchSDK`), and the prebuilt logout screen
 * that remains never had the check. What both still do is read the OAuth
 * request from `window.location.search` once, on first render. An in-app hop
 * should have updated it by then, but that has not been checked in a browser,
 * and getting it wrong breaks MCP login for every client. The reload costs one
 * extra boot on a page a person sees once per connection; drop it only after a
 * dev login through `/login?next=/oauth/authorize…` works without it.
 *
 * Everywhere else navigates in-app — a document reload costs a second boot
 * (auth config, SDK init, `/auth/current`) and therefore a second
 * "Initialising" screen right after the first one finished.
 *
 * This used to be unconditional, to stop an SPA navigation landing on the
 * `_app` guard while `/auth/current` was still in flight and being bounced back
 * to /login. There is no such window left: `isAuthenticated` now settles with
 * the auth SDK rather than a round trip behind it (ENG-1316), so the guard has
 * a real verdict by the time any post-auth navigation reaches it.
 */
export function needsDocumentLoad(href: string): boolean {
  return href.startsWith('/oauth/');
}

/** Post-auth navigation: go to a sanitized `next` path, else home. */
export function goToNextOrHome(
  navigate: ReturnType<typeof useNavigate>,
  next: string | undefined,
) {
  const href = next ?? '/';
  return navigate({ href, reloadDocument: needsDocumentLoad(href) });
}
